import { useMemo } from 'react';

import Grid from '@zenbusiness/zui/Grid';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import AccountDetail from '../AccountDetail';
import { EditButton } from '../EditButtonStyle.styles';
import FieldDetail from '../FieldDetail';

export default function RecipientDetail({
  hasEdit,
  accountHolder,
  bank,
  accountNumber,
  fullAccountNumber,
  address,
  loading,
  handleEdit,
  title = 'To'
}) {
  const cityText = address?.city ?? '';
  const postalCodeText = address?.postalCode ?? '';
  const stateText = address?.state ?? '';
  const streetText = address?.street ?? '';
  const street2Text = address?.street2 ?? '';

  const addressField = useMemo(() => {
    if (!loading && !address) {
      return null;
    }

    return (
      <Grid
        item
        lg={4}
        xs={12}
      >
        <Stack
          direction="column"
          gap="8px"
        >
          <Text
            color="secondary"
            size="xs"
            uppercase
          >
            Address
          </Text>
          {loading ? (
            <Skeleton />
          ) : (
            <>
              <Text
                color="primary"
                size="lg"
              >
                {streetText}
              </Text>
              {street2Text && (
                <Text
                  color="primary"
                  size="lg"
                >
                  {street2Text}
                </Text>
              )}
              <Text
                color="primary"
                size="lg"
              >{`${cityText}, ${stateText} ${postalCodeText}`}</Text>
            </>
          )}
        </Stack>
      </Grid>
    );
  }, [address, cityText, loading, postalCodeText, stateText, street2Text, streetText]);

  return (
    <Grid
      columnSpacing="8px"
      container
      pb={{ xs: '24px', lg: '56px' }}
      pt={{ xs: '24px', lg: '32px' }}
      px={{ xs: '24px', lg: '32px' }}
      rowSpacing={{ xs: '16px', lg: '32px' }}
    >
      <Grid
        item
        xs={12}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Text
            color="primary"
            size="xl"
            weight="medium"
          >
            {title}
          </Text>
          {hasEdit && (
            <EditButton
              color="info"
              onClick={handleEdit}
              size="small"
              variant="text"
            >
              Edit
            </EditButton>
          )}
        </Stack>
      </Grid>
      <FieldDetail
        label="Account holder"
        loading={loading}
        value={accountHolder}
      />
      <FieldDetail
        label="bank"
        loading={loading}
        value={bank}
      />
      <AccountDetail
        accountNumber={accountNumber}
        fullAccountNumber={fullAccountNumber}
        label="Account"
        loading={loading}
      />
      {addressField}
    </Grid>
  );
}

RecipientDetail.propTypes = {
  hasEdit: PropTypes.bool,
  accountHolder: PropTypes.string,
  bank: PropTypes.string,
  accountNumber: PropTypes.string,
  fullAccountNumber: PropTypes.string,
  title: PropTypes.string,
  loading: PropTypes.bool,
  handleEdit: PropTypes.func,
  address: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string,
    postalCode: PropTypes.string,
    state: PropTypes.string,
    street: PropTypes.string,
    street2: PropTypes.string
  })
};
